import {useRef} from 'react'
import {scrollBelowHeader} from '@/utils/scrollBelowHeader.js'
import {arrowRightSm, editPenIcon, distanceIcon, destinationTimeIcon, whiteCar, locationIcon} from '@img/index.js'
import {formatDHM, parseAppZoneDate} from '@/utils/dateUtils'

const ChevronIcon = ({expanded}) => (
    <img src={arrowRightSm} alt=""
         className="w-4.5 h-4.5 transition-transform duration-300"
         style={{transform: `rotate(${expanded ? -90 : 90}deg)`}}/>
)

const EditButton = ({onClick, policy = {canEdit: true, blockedReason: null}}) => (
    <div className="relative shrink-0 group">
        <button type="button" disabled={!policy.canEdit} onClick={onClick}
                className="cursor-pointer w-10 h-10 shrink-0 rounded-full bg-(--text-primary) flex items-center justify-center p-0 border-0 disabled:opacity-50 disabled:cursor-not-allowed">
            <img src={editPenIcon} alt="Edit"/>
        </button>
        {!policy.canEdit && policy.blockedReason && (
            <div className="hidden group-hover:block absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                <p className="mb-0!">{policy.blockedReason}</p>
            </div>
        )}
    </div>
)

function loc(v) { if (!v) return ''; if (typeof v === 'string') return v; return v.address || [v.city, v.state, v.country].filter(Boolean).join(', ') || '' }
function fmtTime(d) { if (!d) return '--'; return new Date(d).toLocaleTimeString('en-US', {hour:'numeric', minute:'2-digit', hour12:true}) }
function fmtDuration(m) { return m ? formatDHM(m) : '--' }

// `readOnly` = the view page (/rides/:id/view): the card renders identically but with
// NO edit affordance at all — not a disabled pencil (that reads as "blocked"), just
// absent. Same distinction in SummaryCards / CarDetailsCard / SpecialRequestCard.
export default function RouteCard({trip, expanded, onToggle, onEdit, collapsible = true, policy = {canEdit: true, blockedReason: null}, title = 'From Airport', summary = 'origin', showBanner = true, includeStopovers = summary !== 'origin', readOnly = false}) {
    const isExpanded = collapsible ? expanded : true
    const rootRef = useRef(null)
    const origin = loc(trip?.origin)
    const destination = loc(trip?.destination)
    const summaryAddress = summary === 'destination' ? destination : origin
    const stopovers = (trip?.stop_overs || []).map(s => ({
        location: loc(s.location || s),
    }))
    const distanceKm = trip?.route?.distance_km
    const durationMin = trip?.route?.duration_minutes
    const legs = trip?.route?.legs || []
    const stopCount = stopovers.length
    // Real instant: the leg clock lines below are DISPLAY, rendered in the viewer's zone.
    // (The picker's own state in DateTimeCard stays on the Toronto wall clock — that's the
    // input side, and it serializes straight back to a naive APP_TIMEZONE string.)
    const baseMs = trip?.scheduled_at ? (parseAppZoneDate(trip.scheduled_at)?.getTime() ?? null) : null
    // Cumulative leg endpoints: legEnds[0] is arrival at stopover 0 (or destination if no stopovers), etc.
    //
    // Built whether or not there is a start time. The DURATIONS come from the route's legs
    // (calculate-route) and need no base — same walk orderDetails does over route_data.legs. Only
    // `time` needs scheduled_at, so it stays null until one exists. This used to sit inside
    // `if (baseMs)`, which meant a Draft with no date/time yet showed no per-leg travel times at
    // all and the drop-off fell back to the WHOLE-trip duration instead of its own leg's.
    const legEnds = []
    let cursor = baseMs
    if (legs.length > 0) {
        for (const leg of legs) {
            const dur = leg.duration_minutes ?? leg.duration_in_minutes ?? (leg.duration_seconds ? Math.round(leg.duration_seconds / 60) : 0)
            if (cursor != null) cursor += dur * 60000
            legEnds.push({time: cursor, durationMin: dur})
        }
    } else if (stopCount > 0 && durationMin > 0) {
        // Fallback: evenly split total duration across legs
        const perLeg = Math.round(durationMin / (stopCount + 1))
        for (let i = 0; i < stopCount + 1; i++) {
            if (cursor != null) cursor += perLeg * 60000
            legEnds.push({time: cursor, durationMin: perLeg})
        }
    }
    const arrivalTime = baseMs && durationMin
        ? fmtTime(new Date(baseMs + durationMin * 60000))
        : null

    // A single stopover row (whiteCar icon + arrival time + address). Same markup
    // whether it renders under the origin (toAirport) or above the destination.
    const renderStop = (stop, i, isLast) => {
        const leg = legEnds[i]
        return (
            <div key={i} className={`flex relative ${isLast ? 'mb-0' : 'mb-6'} min-h-6.5 max-md:flex-col max-md:gap-1`}>
                <img src={whiteCar} alt="" className="absolute top-1 -left-1 w-8 h-7 z-10 bg-white"/>
                <div className="md:w-1/2 w-full pl-10">
                    {/* `leg.time` is null until a start time exists — render the clock line only
                        then, but keep the travel time below it either way. */}
                    {leg?.time != null && <span className="block text-base">{fmtTime(new Date(leg.time))}</span>}
                    {leg?.durationMin > 0 && <span className="block text-(--text-secondary) text-xs">Travel time: {fmtDuration(leg.durationMin)}</span>}
                </div>
                <div className="md:w-1/2 w-full max-md:pl-10"><span className="block text-base">{stop.location}</span></div>
            </div>
        )
    }
    const showStops = includeStopovers && stopovers.length > 0

    return (
        <div ref={rootRef}>
            {showBanner && <span className="inline-block bg-(--text-primary) text-white rounded-full px-5 py-2.5 mb-8">Enter info to reserve return ride</span>}
            <h2 className="font-bold text-xl mb-3">{title}</h2>
            <div className="bg-white px-4 py-1 rounded-(--radius-xl) border border-(--text-primary)">
            <div className="flex-1 min-w-0">
                <div className={`flex items-center gap-5 min-h-[64px] ${collapsible ? 'cursor-pointer' : ''}`} onClick={collapsible ? () => {
                    onToggle()
                    // Expanding brings the title + card to the top of the screen, below the header.
                    if (!isExpanded) setTimeout(() => scrollBelowHeader(rootRef.current), 50)
                } : undefined}>
                    <div className="flex-1 min-w-0 overflow-hidden flex items-center">
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis min-w-0 text-base">{summaryAddress}</span>
                    </div>
                    {collapsible && (
                        <span className="flex items-center shrink-0"><ChevronIcon expanded={isExpanded}/></span>
                    )}
                </div>
                {isExpanded && (
                    <div className="pt-4 pb-3">
                        <div className="relative mb-8 max-h-[150px] overflow-y-auto">
                            <div className="relative overflow-hidden">
                                {summary === 'origin' ? (
                                    /* Start location — plus stopovers below it in the toAirport flow */
                                    <>
                                        {showStops && (
                                            <span className="absolute h-[86%] w-px left-[11px] top-[21px] border-l-2 border-dashed border-(--text-primary)"></span>
                                        )}
                                        <div className={`flex relative min-h-6.5 ${showStops ? 'mb-6' : ''}`}>
                                            <span className="absolute top-0 left-0 w-5 h-full bg-white z-10"></span>
                                            <img src={locationIcon} alt="" className="absolute top-0 left-0.5 w-6 h-6 z-10"/>
                                            <div className="w-full pl-10"><span className="block text-base">{origin}</span></div>
                                        </div>
                                        {showStops && stopovers.map((stop, i) => renderStop(stop, i, i === stopovers.length - 1))}
                                    </>
                                ) : (
                                    /* Drop off field — stopovers (unless moved to Start) + destination */
                                    <>
                                        {showStops && (
                                            <span className="absolute h-[86%] w-px left-[11px] top-[21px] border-l-2 border-dashed border-(--text-primary)"></span>
                                        )}
                                        {showStops && stopovers.map((stop, i) => renderStop(stop, i, false))}
                                        <div className="flex relative mb-0 min-h-6.5 max-md:flex-col max-md:gap-1">
                                            <span className="absolute top-0 left-0 w-5 h-full bg-white z-10"></span>
                                            <img src={locationIcon} alt="" className="absolute top-0 left-0.5 w-6 h-6 z-10"/>
                                            <div className="md:w-1/2 w-full pl-10">
                                                {arrivalTime && <span className="block text-base">{arrivalTime}</span>}
                                                {(() => {
                                                    const lastLegMin = legEnds.length > 0 ? legEnds[legEnds.length - 1].durationMin : durationMin
                                                    return lastLegMin > 0 && <span className="block text-(--text-secondary) text-xs">Travel time: {fmtDuration(lastLegMin)}</span>
                                                })()}
                                            </div>
                                            <div className="md:w-1/2 w-full max-md:pl-10"><span className="block text-base">{destination}</span></div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {/* The origin card's footer holds ONLY the pencil, so in read-only mode
                            the whole row goes rather than leaving an empty strip. */}
                        {!(readOnly && summary === 'origin') && (
                            <div className={`flex items-end gap-6 ${summary === 'origin' ? 'justify-end' : ''}`}>
                                {summary !== 'origin' && (
                                    <>
                                        <button type="button" className="bg-(--bg-grey) border-0 w-full rounded-full py-1.5 text-base flex items-center justify-center cursor-default">
                                            <img src={distanceIcon} alt="" className="mr-1.5 max-h-5.5"/>{distanceKm ? `${distanceKm} km` : '--'}
                                        </button>
                                        <button type="button" className="bg-(--bg-grey) border-0 w-full rounded-full py-1.5 text-base flex items-center justify-center cursor-default">
                                            <img src={destinationTimeIcon} alt="" className="mr-1.5 max-h-5.5"/>{fmtDuration(durationMin)}
                                        </button>
                                    </>
                                )}
                                {!readOnly && <EditButton onClick={onEdit} policy={policy}/>}
                            </div>
                        )}
                    </div>
                )}
            </div>
            </div>
        </div>
    )
}
